"use client";

import Link from "next/link";
import { toast } from "sonner";
import { Children, isValidElement, memo, useEffect, useRef, type ReactNode } from "react";
import { useDeferredValue, useMemo, useState, useTransition } from "react";
import { useAction, useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronUp, Copy, Database, Layers3, Loader2, MapPin, Search, Sparkles, X, Users, Brain, Download, Upload } from "lucide-react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { ContactEnrichmentPreview } from "@/components/contacts/contact-enrichment-preview";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDollarsShort } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { Button } from "../button/button";
import { Text } from "../text/Text";
import { Column } from "../column/Column";
import { Row } from "../row/Row";
import { Input } from "@/components/ui/input";
import parsePhoneNumber from "libphonenumber-js";
import { BucketerApp } from "@/app/marketing/_components/bucketer/BucketerApp";
import { EnrichmentStatus } from "../../activate/_components/enrichment-status";
import Grainient from "../Grainient";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useSidebar } from "@/components/ui/sidebar";
import SettingsContactImportPage from "@/components/settings/settings-contact-import";

interface RunProgress {
  contactsFetched: number;
  contactsEnriched: number;
  addressesResolved: number;
  propertiesLookedUp: number;
  totalContacts: number;
  totalCrmContacts?: number;
  skipTrace?: "done" | "pending" | "running";
  propertyData?: "done" | "pending" | "running";
  equityAnalysis?: "done" | "pending" | "running";
  segmentation?: "done" | "pending" | "running";
  skipTracesAttempted?: number;
  skipTracesFound?: number;
}

interface ContactBucket {
  _id: Id<"bucketerBuckets">;
  bucketId: string;
  name: string;
  description: string;
  priority: number;
  contactCount: number;
  isDisabled: boolean;
}

interface EnrichedContact {
  _id: Id<"bucketerContacts">;
  _creationTime: number;
  rolloutContactId: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  tags: string[];
  stage?: string;
  bucketId?: string;
  flattened: Record<string, string>;
}

interface ContactsViewData {
  crmConnected: boolean;
  latestRun: {
    _id: Id<"bucketerRuns">;
    _creationTime: number;
    status: "bucketing" | "complete" | "enriching" | "failed" | "fetching";
    progress: RunProgress;
    bucketingStep?: string;
    error?: string | null;
  } | null;
  activeRun: {
    _id: Id<"bucketerRuns">;
    _creationTime: number;
    progress: RunProgress;
    valueSummary?: {
      totalPortfolioValue: number;
      commissionPotential: number;
      turnoverRate: number;
      commissionRate: number;
    };
  } | null;
  buckets: ContactBucket[];
  featuredContacts: EnrichedContact[];
  featuredContact: EnrichedContact | null;
  summary: {
    totalEnriched: number;
    totalDatabaseSize: number;
    totalBuckets: number;
    activeBuckets: number;
    contactsWithProperty: number | null;
    contactsWithSkipTrace: number | null;
    primaryMarkets: string[];
    baseFieldCount: number;
    enrichedFieldCount: number;
  } | null;
}


type RunStatus = NonNullable<ContactsViewData["latestRun"]>["status"];
type QuickFilter = "all" | "enriched" | "highEquity";
type PaginatedContactsStatus = "CanLoadMore" | "Exhausted" | "LoadingFirstPage" | "LoadingMore";
type ContactsExportScope = "all" | "enriched" | "loaded";
type ContactsCsvExportStatus = "cancelled" | "completed" | "failed" | "pending" | "running";
interface ContactsCsvExportDoc {
  _id: Id<"bucketerExports">;
  _creationTime: number;
  teamId: Id<"teams">;
  userId: Id<"users">;
  runId: Id<"bucketerRuns">;
  scope: ContactsExportScope;
  status: ContactsCsvExportStatus;
  cancelRequested?: boolean;
  contactIds?: Id<"bucketerContacts">[];
  requestedCount?: number;
  processedCount?: number;
  exportedCount?: number;
  pagesProcessed?: number;
  pageSize?: number;
  estimatedDurationMs?: number;
  startedAt?: number;
  finishedAt?: number;
  durationMs?: number;
  storageId?: Id<"_storage">;
  url?: string;
  sizeBytes?: number;
  error?: string;
}
interface ContactsCsvExportResult {
  storageId: Id<"_storage">;
  url: string | null;
  rowCount: number;
  sizeBytes: number;
  pagesProcessed: number;
  pageSize: number;
  durationMs: number;
  startedAt: number;
  finishedAt: number;
}
interface ContactsExportScopeOption {
  id: ContactsExportScope;
  label: string;
  description: string;
  count: number;
  disabled?: boolean;
}

const DATASET_STATUS_STORAGE_KEY = "better-email.contacts.dataset-status-expanded";
const COLLAPSED_DATASET_STATUS_HEIGHT = 160;
const COLLAPSED_DATASET_STATUS_HEIGHT_HOVERED = COLLAPSED_DATASET_STATUS_HEIGHT * 1.3;
const DEFAULT_EXPORT_BENCHMARK = {
  rowCount: 6618,
  durationMs: 7400,
} as const;
const MIN_EXPORT_ESTIMATE_MS = 5000;
const CONTACTS_GRAINIENT_PROPS = {
  color1: "#ffffff",
  color2: "#bfb3ff",
  color3: "#f4e0a9",
  timeSpeed: 0.35,
  colorBalance: 0.01,
  warpStrength: 0.75,
  warpFrequency: 5,
  warpSpeed: 2,
  warpAmplitude: 50,
  blendAngle: 47,
  blendSoftness: 0.09,
  rotationAmount: 500,
  noiseScale: 1.4,
  grainAmount: 0.09,
  grainScale: 3.8,
  grainAnimated: false,
  contrast: 1.2,
  gamma: 1.3,
  saturation: 1.25,
  centerX: -0.05,
  centerY: 0,
  zoom: 1.3,
  className: "h-full w-full",
} as const;

const ENRICHMENT_SIGNAL_FIELDS = [
  "estimated_value",
  "estimated_equity",
  "equity_percent",
  "ownership_length_months",
  "property_type",
  "owner_occupied",
  "absentee_owner",
  "free_clear",
  "high_equity",
  "vacant",
  "bedrooms",
  "bathrooms",
  "living_sqft",
  "year_built",
  "age_range",
  "gender",
  "occupation",
  "last_sale_price",
] as const;

const RUN_STATUS_META = {
  fetching: {
    label: "Fetching",
    description: "Pulling the latest CRM records into Databank.",
    badgeClass: "border-amber-200/80 bg-amber-50 text-amber-900",
    dotClass: "bg-amber-500",
  },
  enriching: {
    label: "Enriching",
    description: "Appending property, ownership, and homeowner signals to each contact.",
    badgeClass: "border-sky-200/80 bg-sky-50 text-sky-900",
    dotClass: "bg-sky-500",
  },
  bucketing: {
    label: "Bucketing",
    description: "Sorting enriched records into reusable audience segments.",
    badgeClass: "border-violet-200/80 bg-violet-50 text-violet-900",
    dotClass: "bg-violet-500",
  },
  complete: {
    label: "Complete",
    description: "Your database is fully enriched and ready for use.",
    badgeClass: "border-emerald-200/80 bg-emerald-50 text-emerald-900",
    dotClass: "bg-emerald-500",
  },
  failed: {
    label: "Needs attention",
    description: "The most recent enrichment run stopped before it finished.",
    badgeClass: "border-rose-200/80 bg-rose-50 text-rose-900",
    dotClass: "bg-rose-500",
  },
} satisfies Record<
  RunStatus,
  {
    label: string;
    description: string;
    badgeClass: string;
    dotClass: string;
  }
>;

function parseFlatNumber(value?: string) {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseFlatBoolean(value?: string) {
  const normalized = value?.trim().toUpperCase();
  if (normalized === "TRUE") return true;
  if (normalized === "FALSE") return false;
  return null;
}

function formatDurationMs(durationMs: number | null | undefined) {
  if (typeof durationMs !== "number" || durationMs <= 0) return "a few seconds";

  const roundedSeconds = Math.max(1, Math.round(durationMs / 1000));
  if (roundedSeconds < 60) return `${roundedSeconds} second${roundedSeconds === 1 ? "" : "s"}`;

  const minutes = Math.floor(roundedSeconds / 60);
  const seconds = roundedSeconds % 60;
  if (seconds === 0) {
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }

  return `${minutes} minute${minutes === 1 ? "" : "s"} ${seconds} second${seconds === 1 ? "" : "s"}`;
}

function estimateExportDurationMs({
  benchmarkRows,
  benchmarkDurationMs,
  targetRows,
}: {
  benchmarkRows: number | null | undefined;
  benchmarkDurationMs: number | null | undefined;
  targetRows: number | null | undefined;
}) {
  if (
    typeof benchmarkRows !== "number" ||
    benchmarkRows <= 0 ||
    typeof benchmarkDurationMs !== "number" ||
    benchmarkDurationMs <= 0 ||
    typeof targetRows !== "number" ||
    targetRows <= 0
  ) {
    return null;
  }

  return Math.max(MIN_EXPORT_ESTIMATE_MS, Math.round((benchmarkDurationMs / benchmarkRows) * targetRows));
}

function getSimulatedExportProgress({
  elapsedMs,
  estimatedDurationMs,
  isDone,
}: {
  elapsedMs: number;
  estimatedDurationMs: number | null | undefined;
  isDone: boolean;
}) {
  if (isDone) return 100;

  const safeEstimateMs = Math.max(estimatedDurationMs ?? MIN_EXPORT_ESTIMATE_MS, MIN_EXPORT_ESTIMATE_MS);
  if (elapsedMs <= safeEstimateMs) {
    return Math.min(90, (elapsedMs / safeEstimateMs) * 90);
  }

  const overtimeMs = elapsedMs - safeEstimateMs;
  const slowedProgress = 90 + 9 * (1 - Math.exp(-overtimeMs / Math.max(safeEstimateMs * 1.5, 15000)));
  return Math.min(99, slowedProgress);
}

function buildExportFilename(timestamp: number | null | undefined) {
  const date = typeof timestamp === "number" ? new Date(timestamp) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, "0");
  const day = String(safeDate.getDate()).padStart(2, "0");
  return `contacts-export-${year}-${month}-${day}.csv`;
}

async function downloadCsvWithFilename(url: string, filename: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}.`);
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

function formatCoverage(count: number | null, total: number) {
  if (count == null || total <= 0) return "—";
  return `${Math.round((count / total) * 100)}%`;
}

function formatYearsOwned(months?: string) {
  const value = parseFlatNumber(months);
  if (value == null || value <= 0) return null;
  const years = Math.max(1, Math.round(value / 12));
  return `${years} yr${years === 1 ? "" : "s"} owned`;
}

function formatPropertyLine(flattened: Record<string, string>) {
  const city = flattened.property_city || flattened.mail_city;
  const state = flattened.property_state || flattened.mail_state;
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (state) return state;
  return "No property location yet";
}

function formatPropertyMeta(flattened: Record<string, string>) {
  const parts: string[] = [];
  if (flattened.property_type) parts.push(flattened.property_type);

  const absenteeOwner = parseFlatBoolean(flattened.absentee_owner);
  const ownerOccupied = parseFlatBoolean(flattened.owner_occupied);
  if (absenteeOwner === true) {
    parts.push("Absentee owner");
  } else if (ownerOccupied === true) {
    parts.push("Owner occupied");
  } else if (ownerOccupied === false) {
    parts.push("Non-owner occupied");
  }

  return parts.join(" • ") || "Mailing record only";
}

function countEnrichmentSignals(flattened: Record<string, string>) {
  return ENRICHMENT_SIGNAL_FIELDS.filter((field) => Boolean(flattened[field])).length;
}

function getAvatar(contact: EnrichedContact) {
  const displayName = getDisplayName(contact);
  const initials = displayName
    .split(" ")
    .map((name) => name[0])
    .join("");
  return (
    <div className="flex size-10 items-center justify-center bg-linear-to-br from-teal-300 to-teal-500 rounded-full bg-primary/10 ">
      <Text chonky className="text-white">
        {initials}
      </Text>
    </div>
  );
}

function getDisplayName(contact: EnrichedContact) {
  const direct = contact.name?.trim();
  if (direct) return direct;
  const joined = [contact.firstName?.trim(), contact.lastName?.trim()].filter(Boolean).join(" ").trim();
  if (joined) return joined;
  return contact.email || contact.phone || "Unnamed contact";
}

function buildSignalChips(flattened: Record<string, string>, limit = 5) {
  const chips: string[] = [];

  const ownership = formatYearsOwned(flattened.ownership_length_months);
  if (ownership) chips.push(ownership);

  const highEquity = parseFlatBoolean(flattened.high_equity);
  if (highEquity) chips.push("High equity");

  const freeClear = parseFlatBoolean(flattened.free_clear);
  if (freeClear) chips.push("Free & clear");

  const vacant = parseFlatBoolean(flattened.vacant);
  if (vacant) chips.push("Vacant");

  const absenteeOwner = parseFlatBoolean(flattened.absentee_owner);
  if (absenteeOwner) chips.push("Absentee owner");

  const beds = parseFlatNumber(flattened.bedrooms);
  if (beds != null && beds > 0) chips.push(`${beds} bed`);

  const baths = parseFlatNumber(flattened.bathrooms);
  if (baths != null && baths > 0) chips.push(`${baths} bath`);

  const livingSqft = parseFlatNumber(flattened.living_sqft);
  if (livingSqft != null && livingSqft > 0) chips.push(`${Math.round(livingSqft).toLocaleString()} sqft`);

  const yearBuilt = parseFlatNumber(flattened.year_built);
  if (yearBuilt != null && yearBuilt > 0) chips.push(`Built ${yearBuilt}`);

  if (flattened.age_range) chips.push(`Age ${flattened.age_range}`);
  if (flattened.gender) chips.push(flattened.gender);
  if (flattened.occupation) chips.push(flattened.occupation);

  return chips.slice(0, limit);
}

function hasPropertyData(flattened: Record<string, string>) {
  return Boolean(
    flattened.property_city ||
    flattened.property_state ||
    flattened.property_type ||
    flattened.estimated_value ||
    flattened.estimated_equity,
  );
}

function hasHighEquity(flattened: Record<string, string>) {
  if (parseFlatBoolean(flattened.high_equity) === true) return true;
  const equityPercent = parseFlatNumber(flattened.equity_percent);
  return equityPercent != null && equityPercent >= 50;
}

function hasAbsenteeOwner(flattened: Record<string, string>) {
  return parseFlatBoolean(flattened.absentee_owner) === true;
}

function matchesQuickFilter(contact: EnrichedContact, quickFilter: QuickFilter) {
  switch (quickFilter) {
    case "enriched":
      return hasPropertyData(contact.flattened);
    case "highEquity":
      return hasHighEquity(contact.flattened);
    case "all":
    default:
      return true;
  }
}

const ContactsBackground = memo(function ContactsBackground() {
  return <Grainient {...CONTACTS_GRAINIENT_PROPS} />;
});

function ContactsCsvImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="min-w-[80%] sm:max-w-[80%] h-auto max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Import contacts from CSV</DialogTitle>
      </DialogHeader>
      <SettingsContactImportPage />
    </DialogContent>
  </Dialog>;
}

function ContactsCsvExportDialog({
  open,
  onOpenChange,
  exportScope,
  onExportScopeChange,
  exportScopeOptions,
  estimatedDurationMs,
  activeExport,
  exportError,
  isStartingExport,
  isCancellingExport,
  simulatedProgress,
  onConfirmExport,
  onCancelDownload,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exportScope: ContactsExportScope;
  onExportScopeChange: (scope: ContactsExportScope) => void;
  exportScopeOptions: ContactsExportScopeOption[];
  estimatedDurationMs: number | null;
  activeExport: ContactsCsvExportDoc | null | undefined;
  exportError: string | null;
  isStartingExport: boolean;
  isCancellingExport: boolean;
  simulatedProgress: number;
  onConfirmExport: () => void;
  onCancelDownload: () => void;
}) {
  const selectedOption = exportScopeOptions.find((option) => option.id === exportScope) ?? exportScopeOptions[0];
  const isExportRunning =
    isStartingExport || activeExport?.status === "pending" || activeExport?.status === "running";
  const isDismissBlocked = isExportRunning || isCancellingExport;
  const failedMessage = exportError ?? activeExport?.error ?? null;
  const isFailed = Boolean(failedMessage) && activeExport?.status !== "cancelled";
  const progressLabel = activeExport
    ? activeExport.scope === "enriched" && (activeExport.processedCount ?? 0) !== (activeExport.exportedCount ?? 0)
      ? `Scanned ${(activeExport.processedCount ?? 0).toLocaleString()} contacts and added ${(activeExport.exportedCount ?? 0).toLocaleString()} rows.`
      : `Prepared ${(activeExport.exportedCount ?? 0).toLocaleString()} rows across ${(activeExport.pagesProcessed ?? 0).toLocaleString()} pages.`
    : "Starting export…";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isDismissBlocked) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        showClose={!isDismissBlocked}
        className="min-w-[48rem] sm:max-w-[48rem]"
        onEscapeKeyDown={(event) => {
          if (isDismissBlocked) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isDismissBlocked) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {isExportRunning ? "Preparing your CSV" : isFailed ? "Export failed" : "Export contacts as CSV"}
          </DialogTitle>
          <DialogDescription>
            {isExportRunning
              ? `We’re preparing ${selectedOption.label.toLowerCase()} for download.`
              : isFailed
                ? failedMessage
                : "Choose which contacts to include in this export."}
          </DialogDescription>
        </DialogHeader>

        {isExportRunning ? (
          <Column bigGap>
            <Column tightGap className="space-y-0">
              <Row between align>
                <Text sm muted>
                  Estimated time
                </Text>
                <Text sm fat className="tabular-nums text-foreground">
                  {formatDurationMs(activeExport?.estimatedDurationMs ?? estimatedDurationMs)}
                </Text>
              </Row>
              <Progress value={simulatedProgress} className="h-2 bg-subdued" />
              <Row between align>
                <Text sm muted>
                  {progressLabel}
                </Text>
                <Text sm fat className="tabular-nums text-foreground">
                  {`${Math.round(simulatedProgress)}%`}
                </Text>
              </Row>
            </Column>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" disabled={isCancellingExport} onClick={onCancelDownload}>
                {isCancellingExport ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                {isCancellingExport ? "Cancelling…" : "Cancel download"}
              </Button>
            </DialogFooter>
          </Column>
        ) : isFailed ? (
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => { onOpenChange(false); }}>
              Close
            </Button>
            <Button type="button" variant="bold" size="sm" onClick={onConfirmExport}>
              Try again
            </Button>
          </DialogFooter>
        ) : (
          <Column bigGap>
            <RadioGroup value={exportScope} onValueChange={(value) => { onExportScopeChange(value as ContactsExportScope); }}>
              {exportScopeOptions.map((option) => (
                <div
                  key={option.id}
                  role="button"
                  tabIndex={option.disabled ? -1 : 0}
                  onClick={() => {
                    if (!option.disabled) onExportScopeChange(option.id);
                  }}
                  onKeyDown={(event) => {
                    if (option.disabled) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onExportScopeChange(option.id);
                    }
                  }}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-md border border-border/70 px-4 py-3 transition-colors",
                    exportScope === option.id ? "border-primary bg-primary/5" : "bg-white hover:border-primary/40",
                    option.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <RadioGroupItem value={option.id} disabled={option.disabled} className="mt-1" />
                  <Column tightGap className="min-w-0 flex-1 space-y-0">
                    <Row between align className="gap-3">
                      <Text base fat className="text-foreground">
                        {option.label}
                      </Text>
                      <Text sm fat className="tabular-nums text-foreground">
                        {option.count.toLocaleString()}
                      </Text>
                    </Row>
                    <Text sm muted className="text-pretty">
                      {option.description}
                    </Text>
                  </Column>
                </div>
              ))}
            </RadioGroup>

            <div className="rounded-md border border-border/70 bg-subdued/30 px-4 py-3">
              <Row between align>
                <Text sm muted>
                  Estimated download time
                </Text>
                <Text sm fat className="tabular-nums text-foreground">
                  {formatDurationMs(estimatedDurationMs)}
                </Text>
              </Row>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => { onOpenChange(false); }}>
                Cancel
              </Button>
              <Button type="button" variant="bold" size="sm" onClick={onConfirmExport} disabled={selectedOption?.disabled}>
                Download CSV
              </Button>
            </DialogFooter>
          </Column>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function getQuickFilterPaginationState({
  isSearching,
  paginatedStatus,
  quickFilter,
  loadedContactCount,
  filteredContactCount,
}: {
  isSearching: boolean;
  paginatedStatus: PaginatedContactsStatus;
  quickFilter: QuickFilter;
  loadedContactCount: number;
  filteredContactCount: number;
}) {
  const canLoadMoreContacts = !isSearching && paginatedStatus === "CanLoadMore";
  const isLoadingMoreContacts = paginatedStatus === "LoadingMore";
  const isExhausted = paginatedStatus === "Exhausted";

  return {
    canLoadMoreContacts,
    isExhausted,
    isLoadingMoreContacts,
    showLoadedCount: !isSearching && quickFilter !== "all",
    quickFilterExhaustedLoadedPage: canLoadMoreContacts && filteredContactCount === 0 && loadedContactCount > 0,
  };
}

function formatCompactAddress(flattened: Record<string, string>) {
  const city = flattened.mail_city ?? flattened.property_city;
  const state = flattened.mail_state ?? flattened.property_state;
  const street = flattened.mail_street ?? flattened.property_street;
  const zip = flattened.mail_zip ?? flattened.property_zip;

  if (street && city && state && zip) return `${street}, ${city}, ${state} ${zip}`;
  if (street && city && state) return `${street}, ${city}, ${state}`;
  if (street && city) return `${street}, ${city}`;
  if (street) return street;
  if (city) return city;
  if (state) return state;
  if (zip) return zip;
  return formatCompactProperty(flattened);
}

function formatCompactProperty(flattened: Record<string, string>) {
  const line = formatPropertyLine(flattened);
  const meta = formatPropertyMeta(flattened);
  if (line === "No property location yet") return meta;
  if (meta === "Mailing record only") return line;
  return `${line} • ${meta}`;
}

function formatCompactValue(flattened: Record<string, string>) {
  const estimatedValue = parseFlatNumber(flattened.estimated_value);
  const estimatedEquity = parseFlatNumber(flattened.estimated_equity);
  const equityPercent = parseFlatNumber(flattened.equity_percent);

  const primary = estimatedValue != null ? formatDollarsShort(estimatedValue) : "—";
  const secondary =
    equityPercent != null
      ? `${Math.round(equityPercent)}% eq`
      : estimatedEquity != null
        ? `${formatDollarsShort(estimatedEquity)} eq`
        : "—";

  return `${primary} • ${secondary}`;
}

function FilterChip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-9 items-center rounded-full border px-3 text-sm font-medium leading-none transition-colors",
        active
          ? "border-border bg-subdued text-foreground shadow-xs"
          : "border-transparent bg-transparent text-subdued-foreground hover:border-border/70 hover:bg-subdued/50 hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function StatusBadge({ status }: { status: RunStatus }) {
  const meta = RUN_STATUS_META[status];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium shadow-xs",
        meta.badgeClass,
      )}
    >
      <span className={cn("size-2 rounded-full", meta.dotClass)} />
      {meta.label}
    </span>
  );
}

function getEnrichmentActionMeta({
  crmConnected,
  latestRun,
}: {
  crmConnected: boolean;
  latestRun: ContactsViewData["latestRun"];
}) {
  if (!crmConnected) return null;
  if (!latestRun) {
    return {
      label: "Run Databank enrichment",
      pendingLabel: "Starting enrichment…",
      successMessage: "Databank enrichment started.",
    };
  }
  if (latestRun.status === "failed") {
    return {
      label: "Rerun Databank enrichment",
      pendingLabel: "Restarting enrichment…",
      successMessage: "Databank enrichment restarted.",
    };
  }
  return null;
}

function EnrichmentActionButton({
  crmConnected,
  latestRun,
  variant = "bold",
}: {
  crmConnected: boolean;
  latestRun: ContactsViewData["latestRun"];
  variant?: "bold" | "outline";
}) {
  const actionMeta = getEnrichmentActionMeta({ crmConnected, latestRun });
  const startEnrichment = useAction(api.bucketer.actions.startBucketerRunFromActivation);
  const [isStarting, setIsStarting] = useState(false);

  if (!actionMeta) return null;

  const handleStartEnrichment = async () => {
    if (isStarting) return;

    setIsStarting(true);
    try {
      await startEnrichment({});
      toast.success(actionMeta.successMessage);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to start Databank enrichment.");
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      className="w-fit"
      onClick={handleStartEnrichment}
      disabled={isStarting}
    >
      <Row align tightGap>
        {isStarting ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
        <Text sm>{isStarting ? actionMeta.pendingLabel : actionMeta.label}</Text>
      </Row>
    </Button>
  );
}

function ContactMetric({
  icon,
  label,
  value,
  detail,
  link,
  linkText,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail?: string;
  link?: string;
  linkText?: string;
}) {
  return (
    <Column tightGap className="rounded-md border border-border/70 bg-white/80 p-5 shadow-xs backdrop-blur">
      <Column wide>
        <Row align between>
          <Row align className="gap-2 text-foreground" shrink>
            <span className="flex size-8 items-center justify-center rounded-full bg-primary/8 text-primary">
              {icon}
            </span>
            <Text sm className="font-medium text-foreground">
              {label}
            </Text>
          </Row>
          {link ? (
            <Link href={link}>
              <Button variant="link" size="sm">
                <Text sm>{linkText ?? "View"}</Text>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          ) : null}
        </Row>
      </Column>
      <Text xxxl fat className="text-foreground tabular-nums">
        {value}
      </Text>
      {detail ? (
        <Text sm muted className="mt-2 block text-pretty">
          {detail}
        </Text>
      ) : null}
    </Column>
  );
}

function DataLine({ label, value }: { label: string; value: string }) {
  return (
    <Row between align className="gap-4 border-b border-border/50 py-3 last:border-b-0 last:pb-0">
      <Text >{label}</Text>
      <Text fat className="text-right text-foreground tabular-nums">
        {value}
      </Text>
    </Row>
  );
}

function SignalPill({
  children,
  tone = "default",
  className,
}: {
  children: ReactNode;
  tone?: "accent" | "default" | "muted";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-3 py-1 text-sm shadow-xs w-fit line-clamp-1 shrink-0",
        tone === "accent" && "border-primary/20 bg-primary/8 text-primary",
        tone === "muted" && "border-border/60 bg-subdued/60 text-subdued-foreground",
        tone === "default" && "border-border/70 bg-white/90 text-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}

function PrimaryMarketsStrip({ markets }: { markets: string[] }) {
  if (markets.length === 0) return null;

  return (
    <Column tightGap className="space-y-0">
      <Text sm fat>
        Primary markets
      </Text>
      <div className="flex flex-wrap gap-2">
        {markets.map((market) => (
          <SignalPill key={market} tone="default">
            <Row shrink tightGap align>
              <MapPin className="size-4" />
              {market}
            </Row>
          </SignalPill>
        ))}
      </div>
    </Column>
  );
}

function EmptyContactsState({
  latestRun,
  crmConnected,
}: {
  latestRun: ContactsViewData["latestRun"];
  crmConnected: boolean;
}) {
  const showProgressView = latestRun && latestRun.status !== "complete";

  if (showProgressView) {
    return (
      <div className="mx-auto h-full w-full px-4 py-6 antialiased sm:px-6 lg:px-8 xl:px-10">
        <EnrichmentStatus hideActions={true} hideSample={false} hideStats={false} hideProgress={false} />
      </div>
    );
  }

  const statusMeta = latestRun ? RUN_STATUS_META[latestRun.status] : null;

  return (
    <div className="mx-auto w-full px-4 py-6 antialiased sm:px-6 lg:px-8 xl:px-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.2, 0, 0, 1] }}
        className="w-full overflow-hidden rounded-lg border border-black/5 bg-white shadow-xs"
      >
        <div className="grid lg:grid-cols-[minmax(0,21fr)_19fr]">
          <Column className="gap-6 p-6 sm:p-8 lg:pr-10" start>
            <Row between align className="gap-4">
              <Row align className="gap-3">
                <div className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Database className="size-5" />
                </div>
                <Column noGap>
                  <Text sm fat className="text-foreground">
                    Contacts
                  </Text>
                  <Text sm muted className="text-pretty">
                    Enriched search and audience review
                  </Text>
                </Column>
              </Row>
              {latestRun ? <StatusBadge status={latestRun.status} /> : null}
            </Row>

            <Column className="gap-3" start>
              <h1 className="max-w-[48ch] text-3xl font-semibold tracking-tight text-balance text-foreground sm:text-2xl">
                Your enriched contacts will appear here when the enrichment process completes.
              </h1>
              <EnrichmentActionButton crmConnected={crmConnected} latestRun={latestRun} />
            </Column>

            <div className="border-t border-black/5 pt-6">
              <ContactEnrichmentPreview
                title="What an enriched contact looks like"
                description="Flip between the original CRM record and the enriched version to see the extra homeowner, property, and verification signals this page will unlock."
              />
            </div>
          </Column>

          <div className="border-t border-black/5 px-6 py-6 sm:px-8 sm:py-8 lg:border-l lg:border-t-0">
            <Column className="gap-5" start>
              <Column className="gap-1" start>
                <Text sm fat className="text-foreground">
                  Current status
                </Text>
                <Text sm muted className="text-pretty">
                  The contacts view is driven by your most recent Databank enrichment run.
                </Text>
              </Column>

              <Column noGap className="gap-3" start>
                <Text xl fat className="max-w-[18ch] text-balance text-foreground">
                  {statusMeta?.description ?? "No enrichment run has been created yet."}
                </Text>
                {latestRun?.bucketingStep ? (
                  <Text sm muted className="text-pretty">
                    {latestRun.bucketingStep}
                  </Text>
                ) : (
                  <Text sm muted className="text-pretty">
                    {crmConnected
                      ? "Run Databank enrichment to populate this page with searchable, enriched contacts."
                      : "Connect your CRM to populate this page with searchable, enriched contacts."}
                  </Text>
                )}
                {latestRun?.error ? (
                  <Text sm className="text-destructive text-pretty">
                    {latestRun.error}
                  </Text>
                ) : null}
                {!crmConnected && !latestRun ? (
                  <Text sm muted className="text-pretty">
                    Once your CRM is connected, you can start Databank enrichment from here.
                  </Text>
                ) : null}
              </Column>

              <Column noGap className="divide-y divide-black/5 border-t border-black/5 pt-1" start>
                <Column className="gap-1 py-4" start>
                  <Text sm fat>
                    Search and filter
                  </Text>
                  <Text sm muted className="text-pretty">
                    Find specific contacts or narrow the view by bucket once the latest run lands here.
                  </Text>
                </Column>
                <Column className="gap-1 py-4" start>
                  <Text sm fat>
                    Inspect enrichment data
                  </Text>
                  <Text sm muted className="text-pretty">
                    Review the property, ownership, and homeowner signals that were appended to each record.
                  </Text>
                </Column>
                <Column className="gap-1 pt-4" start>
                  <Text sm fat>
                    Use bucketed audiences
                  </Text>
                  <Text sm muted className="text-pretty">
                    Move from contacts into Databank buckets when you are ready to build or refine audiences.
                  </Text>
                </Column>
              </Column>
            </Column>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function CopyableTableCell({ children }: { children: ReactNode }) {
  const extractText = (node: ReactNode): string => {
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (!node || typeof node === "boolean") return "";
    if (Array.isArray(node)) return node.map((child) => extractText(child)).join("");
    if (isValidElement<{ children?: ReactNode }>(node)) return extractText(node.props.children);
    return "";
  };

  const textString = Children.toArray(children)
    .map((child) => extractText(child))
    .join("");

  const handleCopy = async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(textString);
      toast.success(`${textString} copied.`);
    } catch {
      toast.error(`Failed to copy ${textString}.`);
    }
  };

  return (
    <Row
      aria-label={`Copy ${textString}`}
      onClick={handleCopy}
      align
      tightGap
      className="text-left group cursor-pointer active:translate-y-0.5"
    >
      {children}
      <Copy className="size-4 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 group-active:text-primary" />
    </Row>
  );
}

function ContactsTable({
  contacts,
  bucketById,
  selectedContact,
  onSelectContact,
}: {
  contacts: EnrichedContact[];
  bucketById: Map<string, ContactBucket>;
  selectedContact: EnrichedContact | null;
  onSelectContact: (contact: EnrichedContact | null) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border shadow-xs bg-white">
      <div className="h-[clamp(24rem,70vh,96rem)] overflow-auto overscroll-contain">
        <table className="min-w-[1180px] w-full table-fixed text-sm leading-5">
          <thead className="sticky top-0 z-10 bg-[#fafaf8]">
            <tr className="border-b border-border/70">
              <th className="w-[14rem] px-4 py-3 text-left text-sm font-semibold text-foreground">Name</th>
              <th className="w-[8rem] px-4 py-3 text-left text-sm font-semibold text-foreground">Enriched</th>
              <th className="w-[12rem] px-4 py-3 text-left text-sm font-semibold text-foreground">Phone</th>
              <th className="w-[18rem] px-4 py-3 text-left text-sm font-semibold text-foreground">Email</th>
              <th className="w-[24rem] px-4 py-3 text-left text-sm font-semibold text-foreground">Property</th>
              <th className="w-[12rem] px-4 py-3 text-left text-sm font-semibold text-foreground">Value</th>
              <th className="w-[15rem] px-4 py-3 text-left text-sm font-semibold text-foreground">Bucket</th>
              <th className="w-[12rem] px-4 py-3 text-left text-sm font-semibold text-foreground">Enrichment</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => {
              const bucket = contact.bucketId ? bucketById.get(contact.bucketId) : null;
              const flattened = contact.flattened;
              const signals = buildSignalChips(flattened, 1);
              const totalSignalCount = countEnrichmentSignals(flattened);
              const isSelected = selectedContact?._id === contact._id;

              return (
                <tr
                  key={contact._id}
                  className={cn(
                    "cursor-pointer border-b border-border/60 transition-colors last:border-b-0 hover:bg-primary/5",
                    isSelected ? "bg-primary/8" : "hover:bg-subdued/30",
                  )}
                  onClick={() => { onSelectContact(isSelected ? null : contact); }}
                >
                  <td className="px-4 py-3 align-middle">
                    <div className="flex min-w-0 gap-2">
                      <CopyableTableCell>
                        <Text sm className="truncate font-medium text-foreground">
                          {getDisplayName(contact)}
                        </Text>
                      </CopyableTableCell>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-middle text-foreground tabular-nums">
                    <span className="flex truncate">
                      {totalSignalCount > 0 ? (
                        <Check className="size-6 text-teal-500" />
                      ) : (
                        <X className="size-6 text-muted-foreground" />
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-middle text-foreground tabular-nums">
                    <CopyableTableCell>
                      <span className="block truncate">
                        {contact.phone
                          ? (parsePhoneNumber(contact.phone, { defaultCountry: "US" })?.formatNational() ?? "—")
                          : "—"}
                      </span>
                    </CopyableTableCell>
                  </td>
                  <td className="px-4 py-3 align-middle text-foreground">
                    <CopyableTableCell>
                      <span className="block truncate">{contact.email || "—"}</span>
                    </CopyableTableCell>
                  </td>
                  <td className="px-4 py-3 align-middle text-foreground">
                    <CopyableTableCell>
                      <span className="block truncate">{formatCompactAddress(flattened)}</span>
                    </CopyableTableCell>
                  </td>
                  <td className="px-4 py-3 align-middle text-foreground tabular-nums">
                    <CopyableTableCell>
                      <span className="block truncate">{formatCompactValue(flattened)}</span>
                    </CopyableTableCell>
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <CopyableTableCell>
                      <span
                        className={cn(
                          "inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-xs font-medium leading-none",
                          bucket?.isDisabled
                            ? "bg-subdued text-subdued-foreground"
                            : bucket
                              ? "bg-primary/10 text-primary"
                              : "bg-subdued/70 text-subdued-foreground",
                        )}
                      >
                        <span className="truncate">{bucket?.name ?? "Unbucketed"}</span>
                      </span>
                    </CopyableTableCell>
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <CopyableTableCell>
                      {signals.length > 0 ? (
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                            {signals[0]}
                          </span>
                          {totalSignalCount > 1 ? (
                            <span className="truncate text-xs text-foreground">{`+${totalSignalCount - 1} more`}</span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-subdued-foreground">—</span>
                      )}
                    </CopyableTableCell>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FeaturedContactShowcase({
  contact,
  bucket,
  signalCount,
  signals,
  index,
  total,
  onPrevious,
  onNext,
}: {
  contact: EnrichedContact;
  bucket: ContactBucket | null;
  signalCount: number;
  signals: string[];
  index: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const estimatedValue = parseFlatNumber(contact.flattened.estimated_value);
  const estimatedEquity = parseFlatNumber(contact.flattened.estimated_equity);
  const equityPercent = parseFlatNumber(contact.flattened.equity_percent);
  const hiddenSignals = Math.max(0, signalCount - signals.length);
  const baseFields = [contact.email, contact.phone, formatPropertyLine(contact.flattened)].filter(
    (field): field is string => Boolean(field),
  );

  return (
    <Column wide className="rounded-md border border-border/70 bg-white/85 p-6 shadow-xs backdrop-blur w-full">
      <Row between start>
        <Column wide>
          <Text fat>
            Enrichment samples
          </Text>

          {total > 1 ? (
            <Row between tightGap>
              <Button type="button" variant="ghost" size="sm" disabled={index === 0} onClick={onPrevious}>
                <ArrowLeft className="size-4" />
                Previous
              </Button>
              <Text sm muted>{`${index + 1} of ${total}`}</Text>
              <Button type="button" variant="ghost" size="sm" onClick={onNext}>
                Next
                <ArrowRight className="size-4" />
              </Button>
            </Row>
          ) : null}
          <Separator className="mb-2" />
        </Column>
      </Row>
      <Row align between>
        <Row align>
          {getAvatar(contact)}
          <Column noGap>
            <Text xl chonky className="block text-foreground">
              {getDisplayName(contact)}
            </Text>

            <Text sm subdued className="text-pretty line-clamp-1 truncate">
              {contact.email || "No email"}
              {contact.phone ? ` • ${contact.phone}` : ""}
            </Text>
          </Column>
        </Row>
      </Row>
      <Column bigGap>
        <Column wide>
          <div className="mt-4 flex flex-wrap gap-2">
            {contact.stage ? <SignalPill tone="muted">{`Stage • ${contact.stage}`}</SignalPill> : null}
            <SignalPill tone={bucket ? (bucket.isDisabled ? "muted" : "accent") : "default"}>
              {bucket?.name ?? "Unbucketed"}
            </SignalPill>
            <SignalPill tone="default">{formatPropertyMeta(contact.flattened)}</SignalPill>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Column tightGap className="rounded-md border border-border/60 bg-[#f8faf9] p-4 py-2 justify-between">
              <Text sm className="font-medium text-subdued-foreground">
                Property snapshot
              </Text>
              <Text base fat className="block text-foreground">
                {formatPropertyLine(contact.flattened)}
              </Text>
              <Text sm muted className=" block text-pretty">
                {formatPropertyMeta(contact.flattened)}
              </Text>
            </Column>

            <Column tightGap className="rounded-md border border-border/60 bg-[#f8faf9] p-4 py-2 justify-between">
              <Text sm className="font-medium text-subdued-foreground">
                Value snapshot
              </Text>
              <Text base fat className="mt-2 block text-foreground tabular-nums">
                {estimatedValue != null ? formatDollarsShort(estimatedValue) : "No valuation yet"}
              </Text>
              <Text sm muted className="mt-1 block tabular-nums">
                {equityPercent != null
                  ? `${Math.round(equityPercent)}% equity`
                  : estimatedEquity != null
                    ? `${formatDollarsShort(estimatedEquity)} equity`
                    : "No equity estimate"}
              </Text>
            </Column>
          </div>

          {baseFields.length > 0 ? (
            <Column tightGap className="mt-5 space-y-0">
              <Text sm className="font-medium text-subdued-foreground">
                Base record
              </Text>
              <div className="flex flex-wrap gap-2">
                {baseFields.map((field) => (
                  <SignalPill key={`${contact._id}-${field}`} tone="muted">
                    {field}
                  </SignalPill>
                ))}
              </div>
            </Column>
          ) : null}
        </Column>
        <Column>
          <Column bigGap className="rounded-md border border-border p-6 shadow-xs">
            <Column>
              <Column tightGap className="space-y-0">
                <Text fat>
                  Contact Enrichment
                </Text>
              </Column>
              <Text>Better Email added <span className="text-primary font-semibold">{signalCount} additional data points</span> to this contact.</Text>
            </Column>

            <div className="flex flex-wrap gap-2">
              {signals.map((signal) => (
                <SignalPill key={`featured-${signal}`} tone="accent">
                  {signal}
                </SignalPill>
              ))}
              {hiddenSignals > 0 ? (
                <SignalPill tone="muted">{`+${hiddenSignals} additional signals`}</SignalPill>
              ) : null}
            </div>

            <Column>
              <div className="rounded-md border border-border bg-white/75 p-4">
                <Row align tightGap>
                  <Users className="size-4 text-subdued-foreground" />
                  <Text sm subdued>
                    Bucket status
                  </Text>
                </Row>
                <Text base fat className="mt-2 block text-foreground">
                  {bucket?.isDisabled ? "Excluded from sending" : bucket ? "Active audience bucket" : "No bucket yet"}
                </Text>
                <Text sm muted className="mt-1 block text-pretty">
                  {bucket?.description ?? "This contact has not been assigned to a reusable audience bucket yet."}
                </Text>
              </div>

              <div className="rounded-md border border-border bg-white/75 p-4">
                <Row align tightGap>
                  <Brain className="size-4 text-subdued-foreground" />
                  <Text sm subdued>
                    How we use this data
                  </Text>
                </Row>
                <Text base fat className="mt-2 block text-foreground">
                  {formatPropertyMeta(contact.flattened)}
                </Text>
                <Text sm muted className="mt-1 block text-pretty">
                  Signals like equity, tenure, occupancy, and property details can now drive targeting decisions.
                </Text>
              </div>
            </Column>
          </Column>
        </Column>
      </Column>
    </Column>
  );
}

function LoadedContactsView({ view }: { view: ContactsViewData }) {
  const [selectedBucketId, setSelectedBucketId] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("enriched");
  const [featuredContactIndex, setFeaturedContactIndex] = useState(0);
  const [selectedContact, setSelectedContact] = useState<EnrichedContact | null>(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isDatasetStatusDialogOpen, setIsDatasetStatusDialogOpen] = useState(false);
  const [isEnrichmentSamplesDialogOpen, setIsEnrichmentSamplesDialogOpen] = useState(false);
  const [isImportCsvDialogOpen, setIsImportCsvDialogOpen] = useState(false);
  const [selectedExportScope, setSelectedExportScope] = useState<ContactsExportScope>("all");
  const [activeExportId, setActiveExportId] = useState<Id<"bucketerExports"> | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isStartingExport, setIsStartingExport] = useState(false);
  const [isCancellingExport, setIsCancellingExport] = useState(false);
  const [exportElapsedMs, setExportElapsedMs] = useState(0);
  const [isDatasetStatusExpanded, setIsDatasetStatusExpanded] = useState(true);
  const [isDatasetStatusHovered, setIsDatasetStatusHovered] = useState(false);
  const [hasLoadedDatasetStatusState, setHasLoadedDatasetStatusState] = useState(false);
  const cancelledExportIdsRef = useRef<Set<string>>(new Set());
  const cancelBeforeStartRef = useRef(false);
  const [isPending, startTransition] = useTransition();
  const createContactsCsvExport = useMutation(api.bucketer.export.createContactsCsvExport);
  const cancelContactsCsvExport = useMutation(api.bucketer.export.cancelContactsCsvExport);
  const runContactsCsvExport = useAction(api.bucketer.export.runContactsCsvExport);

  const deferredSearch = useDeferredValue(searchQuery.trim());
  const bucketFilter = selectedBucketId === "all" ? undefined : selectedBucketId;

  const paginated = usePaginatedQuery(
    api.bucketer.queries.getEnrichedContactsPage,
    {
      runId: view.activeRun!._id,
      bucketId: bucketFilter,
    },
    { initialNumItems: 25 },
  );

  const searchResults = useQuery(
    api.bucketer.queries.searchEnrichedContacts,
    deferredSearch
      ? {
        runId: view.activeRun!._id,
        bucketId: bucketFilter,
        searchQuery: deferredSearch,
        limit: 50,
      }
      : "skip",
  ) as EnrichedContact[] | undefined;
  const activeExport = useQuery(
    api.bucketer.export.getContactsCsvExport,
    activeExportId ? { exportId: activeExportId } : "skip",
  );

  const bucketById = useMemo(() => new Map(view.buckets.map((bucket) => [bucket.bucketId, bucket])), [view.buckets]);

  const contacts = deferredSearch ? (searchResults ?? []) : (paginated.results as EnrichedContact[]);
  const filteredContacts = useMemo(
    () => contacts.filter((contact) => matchesQuickFilter(contact, quickFilter)),
    [contacts, quickFilter],
  );
  const { canLoadMoreContacts, isLoadingMoreContacts, isExhausted, quickFilterExhaustedLoadedPage, showLoadedCount } = getQuickFilterPaginationState({
    isSearching: Boolean(deferredSearch),
    paginatedStatus: paginated.status,
    quickFilter,
    loadedContactCount: contacts.length,
    filteredContactCount: filteredContacts.length
  });

  const selectedBucket = bucketFilter ? (bucketById.get(bucketFilter) ?? null) : null;

  const latestRunIsCurrent = view.latestRun?._id === view.activeRun?._id;
  const propertyCoverage = formatCoverage(view.summary?.contactsWithProperty ?? null, view.summary?.totalEnriched ?? 0);
  const skipTraceCoverage = formatCoverage(
    view.activeRun?.progress.skipTracesFound ?? null,
    view.summary?.totalEnriched ?? 0,
  );
  const defaultFeaturedContacts = useMemo(
    () => (view.featuredContacts.length > 0 ? view.featuredContacts : view.featuredContact ? [view.featuredContact] : []),
    [view.featuredContact, view.featuredContacts],
  );
  const featuredContacts = useMemo(
    () =>
      selectedContact
        ? [selectedContact, ...defaultFeaturedContacts.filter((contact) => contact._id !== selectedContact._id)]
        : defaultFeaturedContacts,
    [defaultFeaturedContacts, selectedContact],
  );
  const activeFeaturedContact = useMemo(() => {
    if (featuredContacts.length === 0) return null;

    const normalizedIndex = featuredContactIndex % featuredContacts.length;
    return featuredContacts[normalizedIndex] ?? null;
  }, [featuredContactIndex, featuredContacts]);
  const activeFeaturedBucket = useMemo(
    () => (activeFeaturedContact?.bucketId ? (bucketById.get(activeFeaturedContact.bucketId) ?? null) : null),
    [activeFeaturedContact, bucketById],
  );
  const featuredSignals = useMemo(
    () => (activeFeaturedContact ? buildSignalChips(activeFeaturedContact.flattened, 8) : []),
    [activeFeaturedContact],
  );
  const featuredSignalCount = useMemo(
    () => (activeFeaturedContact ? countEnrichmentSignals(activeFeaturedContact.flattened) : 0),
    [activeFeaturedContact],
  );
  const valueSummary = useMemo(() => view.activeRun?.valueSummary, [view.activeRun?.valueSummary]);
  const totalNewDataPoints = useMemo(
    () => (view.summary?.enrichedFieldCount || 1) * (view.summary?.totalEnriched ?? 1),
    [view.summary?.enrichedFieldCount, view.summary?.totalEnriched],
  );

  useEffect(() => {
    setSelectedContact(null);
    setFeaturedContactIndex(0);
  }, [view.activeRun?._id]);

  useEffect(() => {
    setFeaturedContactIndex(0);
  }, [selectedContact?._id]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const storedValue = window.localStorage.getItem(DATASET_STATUS_STORAGE_KEY);
      if (storedValue != null) {
        setIsDatasetStatusExpanded(storedValue === "true");
      }
    } catch (error) {
      console.error("Failed to load dataset status state from localStorage:", error);
    } finally {
      setHasLoadedDatasetStatusState(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hasLoadedDatasetStatusState) return;

    try {
      window.localStorage.setItem(DATASET_STATUS_STORAGE_KEY, String(isDatasetStatusExpanded));
    } catch (error) {
      console.error("Failed to save dataset status state to localStorage:", error);
    }
  }, [hasLoadedDatasetStatusState, isDatasetStatusExpanded]);

  useEffect(() => {
    if (selectedContact) {
      setIsDatasetStatusExpanded(false);
    }
  }, [selectedContact]);

  useEffect(() => {
    const exportIsRunning =
      isStartingExport || activeExport?.status === "pending" || activeExport?.status === "running";

    if (!exportIsRunning) {
      setExportElapsedMs(0);
      return;
    }

    let animationFrame = 0;
    const startedAt = performance.now();

    const tick = (now: number) => {
      setExportElapsedMs(now - startedAt);
      animationFrame = window.requestAnimationFrame(tick);
    };

    animationFrame = window.requestAnimationFrame(tick);
    return () => { window.cancelAnimationFrame(animationFrame); };
  }, [activeExport?._id, activeExport?.status, isStartingExport]);

  const quickFilters: { id: QuickFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "enriched", label: "Enriched" },
    { id: "highEquity", label: "High equity" },
  ];
  const hasActiveTableFilters = Boolean(bucketFilter || quickFilter !== "all");

  const searchResultsCount = searchResults?.length ?? 0;
  const searchResultsAtMax = searchResultsCount === 50;
  const hasSearchResults = searchResultsCount > 0;
  const currentResultsCount =
    searchResultsAtMax || hasActiveTableFilters
      ? "many"
      : hasSearchResults
        ? searchResultsCount.toLocaleString()
        : (deferredSearch ? filteredContacts.length : (view.summary?.totalDatabaseSize ?? 0)).toLocaleString();
  const loadedContactIds = useMemo(() => filteredContacts.map((contact) => contact._id), [filteredContacts]);
  const allContactsExportCount = view.activeRun?.progress.totalContacts ?? 0;
  const enrichedContactsExportCount = view.summary?.contactsWithProperty ?? 0;
  const loadedContactsExportCount = filteredContacts.length;
  const exportScopeOptions: ContactsExportScopeOption[] = [
    {
      id: "all",
      label: "All contacts",
      description: "Every contact in the current completed dataset.",
      count: allContactsExportCount,
      disabled: allContactsExportCount <= 0,
    },
    {
      id: "enriched",
      label: "All enriched contacts",
      description: "Only contacts that already have property data appended.",
      count: enrichedContactsExportCount,
      disabled: enrichedContactsExportCount <= 0,
    },
    {
      id: "loaded",
      label: `Currently loaded (${loadedContactsExportCount.toLocaleString()} contacts)`,
      description: "Exactly what is loaded in the table right now, including the current search and filters.",
      count: loadedContactsExportCount,
      disabled: loadedContactsExportCount <= 0,
    },
  ];
  const selectedExportOption =
    exportScopeOptions.find((option) => option.id === selectedExportScope) ?? exportScopeOptions[0];
  const estimatedExportDurationMs = estimateExportDurationMs({
    benchmarkRows: DEFAULT_EXPORT_BENCHMARK.rowCount,
    benchmarkDurationMs: DEFAULT_EXPORT_BENCHMARK.durationMs,
    targetRows: selectedExportOption?.count,
  });
  const isExportRunning =
    isStartingExport || activeExport?.status === "pending" || activeExport?.status === "running";
  const simulatedExportProgress = getSimulatedExportProgress({
    elapsedMs: exportElapsedMs,
    estimatedDurationMs: activeExport?.estimatedDurationMs ?? estimatedExportDurationMs,
    isDone: activeExport?.status === "completed",
  });

  const resetExportDialog = () => {
    setIsExportDialogOpen(false);
    setActiveExportId(null);
    setExportError(null);
    setIsStartingExport(false);
    setIsCancellingExport(false);
    setExportElapsedMs(0);
    cancelBeforeStartRef.current = false;
  };

  const handleToggleDatasetStatusDialog = () => {
    setIsDatasetStatusDialogOpen(!isDatasetStatusDialogOpen);
    if (!isDatasetStatusDialogOpen) {
      setIsEnrichmentSamplesDialogOpen(false);
      setIsImportCsvDialogOpen(false);
    }
  };

  const handleToggleEnrichmentSamplesDialog = () => {
    setIsEnrichmentSamplesDialogOpen(!isEnrichmentSamplesDialogOpen);
    if (!isEnrichmentSamplesDialogOpen) {
      setIsImportCsvDialogOpen(false);
      setIsDatasetStatusDialogOpen(false);
    }
  };

  const handleToggleImportCsvDialog = () => {
    setIsImportCsvDialogOpen(!isImportCsvDialogOpen);
    if (!isImportCsvDialogOpen) {
      setIsDatasetStatusDialogOpen(false);
      setIsEnrichmentSamplesDialogOpen(false);
    }
  };

  const handleExportDialogOpenChange = (open: boolean) => {
    if (!open && isExportRunning) return;
    if (!open) {
      resetExportDialog();
      return;
    }
    setIsExportDialogOpen(true);
  };

  const handleOpenExportDialog = () => {
    const shouldDefaultToLoaded = (Boolean(deferredSearch) || hasActiveTableFilters) && loadedContactsExportCount > 0;
    setSelectedExportScope(shouldDefaultToLoaded ? "loaded" : "all");
    setExportError(null);
    setIsExportDialogOpen(true);
  };

  const handleConfirmExport = async () => {
    if (!view.activeRun) return;

    setExportError(null);
    setIsStartingExport(true);
    let exportId: Id<"bucketerExports"> | null = null;

    try {
      exportId = await createContactsCsvExport({
        runId: view.activeRun._id,
        scope: selectedExportScope,
        contactIds: selectedExportScope === "loaded" ? loadedContactIds : undefined,
        estimatedDurationMs: estimatedExportDurationMs ?? undefined,
      });

      setActiveExportId(exportId);

      if (cancelBeforeStartRef.current) {
        cancelledExportIdsRef.current.add(exportId);
        await cancelContactsCsvExport({ exportId });
        toast.message("CSV export cancelled.");
        resetExportDialog();
        return;
      }

      const result = (await runContactsCsvExport({ exportId }));

      if (cancelledExportIdsRef.current.has(exportId)) {
        cancelledExportIdsRef.current.delete(exportId);
        return;
      }

      if (result.url) {
        const filename = buildExportFilename(result.finishedAt);
        try {
          await downloadCsvWithFilename(result.url, filename);
        } catch {
          window.open(result.url, "_blank", "noopener,noreferrer");
        }
      }

      toast.success(
        `Export ready: ${result.rowCount.toLocaleString()} rows in ${formatDurationMs(result.durationMs)}.`,
      );
      resetExportDialog();
    } catch (error) {
      if (
        (exportId && cancelledExportIdsRef.current.has(exportId)) ||
        (error instanceof Error && error.message === "Export cancelled.")
      ) {
        if (exportId) {
          cancelledExportIdsRef.current.delete(exportId);
        }
        return;
      }

      const message = error instanceof Error ? error.message : "Failed to export CSV.";
      setExportError(message);
      toast.error(message);
    } finally {
      setIsStartingExport(false);
    }
  };

  const handleCancelExport = async () => {
    setIsCancellingExport(true);

    if (!activeExportId) {
      cancelBeforeStartRef.current = true;
      return;
    }

    cancelledExportIdsRef.current.add(activeExportId);

    try {
      await cancelContactsCsvExport({ exportId: activeExportId });
      toast.message("CSV export cancelled.");
      resetExportDialog();
    } catch (error) {
      cancelledExportIdsRef.current.delete(activeExportId);
      setIsCancellingExport(false);
      toast.error(error instanceof Error ? error.message : "Failed to cancel CSV export.");
    }
  };

  return (
    <div className="relative overflow-hidden">
      <ContactsCsvImportDialog
        open={isImportCsvDialogOpen}
        onOpenChange={handleToggleImportCsvDialog}
      />
      <ContactsCsvExportDialog
        open={isExportDialogOpen}
        onOpenChange={handleExportDialogOpenChange}
        exportScope={selectedExportScope}
        onExportScopeChange={setSelectedExportScope}
        exportScopeOptions={exportScopeOptions}
        estimatedDurationMs={estimatedExportDurationMs}
        activeExport={activeExport}
        exportError={exportError}
        isStartingExport={isStartingExport}
        isCancellingExport={isCancellingExport}
        simulatedProgress={simulatedExportProgress}
        onConfirmExport={handleConfirmExport}
        onCancelDownload={handleCancelExport}
      />
      {/* <div className="pointer-events-none absolute inset-x-0 top-0 h-full">
        <ContactsBackground />
      </div> */}
      <div className="relative flex w-full min-w-0 flex-col gap-6 px-4 py-6 antialiased sm:px-6 lg:px-8 xl:px-10 xl:py-8">
        <Column>
          <Row align between className="flex-none">
            <Text xxl fat>
              Contacts
            </Text>
            <Row align justifyEnd>
              <div className="relative w-full max-w-[300px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subdued-foreground" />
                <Input
                  aria-label="Search contacts"
                  inputSize="sm"
                  name="contact_search"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    startTransition(() => { setSearchQuery(nextValue); });
                  }}
                />
              </div>

              <Row shrink>
                <Row align tightGap className="pl-6">
                  <Label htmlFor="enriched-only-switch" className="flex"> <Text sm >Enriched only</Text></Label>
                  <Switch id="enriched-only-switch" checked={quickFilter === "enriched"} onCheckedChange={(checked) => {
                    startTransition(() => { setQuickFilter(checked ? "enriched" : "all"); });
                  }} />
                </Row>
                <Row shrink>
                  <Select
                    value={selectedBucketId}
                    onValueChange={(value) => {
                      startTransition(() => { setSelectedBucketId(value); });
                    }}
                  >
                    <SelectTrigger aria-label="Filter contacts by bucket" className="h-9 w-full rounded-full bg-white hover:border-primary/70 hover:bg-primary/5 pb-2.5">
                      <SelectValue placeholder="All buckets" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><Text sm>All buckets</Text></SelectItem>
                      {view.buckets.map((bucket) => (
                        <SelectItem key={bucket.bucketId} value={bucket.bucketId}>
                          <Text sm>{bucket.name} ({bucket.contactCount.toLocaleString()})</Text>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Row>
              </Row>
            </Row>
          </Row>

          <div className="flex flex-col gap-0 pb-3 pt-8">
            <Row align between>
              <Row shrink align tightGap>
                <Text sm className="tabular-nums">Showing <span className="font-bold">{filteredContacts.length.toLocaleString()}</span> contacts of {currentResultsCount}</Text>
                <Button size="sm" variant="link" onClick={() => { paginated.loadMore(50); }} disabled={isExhausted || isLoadingMoreContacts || searchResultsCount > 0}>
                  <Row align>
                    {isExhausted ? "No more contacts" : hasSearchResults ? "" : "Load more"}
                  </Row>
                </Button>
              </Row>
              <Row shrink align tightGap>
                <Button
                  size="sm"
                  variant="link"
                  onClick={handleToggleDatasetStatusDialog}
                >
                  Dataset status
                  <ChevronDown className={cn("size-4 shrink-0 transition-transform", isDatasetStatusDialogOpen && "-rotate-180")} />
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={handleToggleImportCsvDialog}
                >
                  Import CSV
                  <Upload className="size-4 shrink-0" />
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={handleOpenExportDialog}
                  disabled={allContactsExportCount <= 0}
                >
                  Export CSV
                  <Download className="size-4 shrink-0" />
                </Button>
              </Row>
            </Row>
            <div className="flex flex-col gap-3">
              <AnimatePresence mode="wait">
                {isDatasetStatusDialogOpen && (
                  <motion.div
                    key="dataset-status-dialog"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25, ease: [0.2, 0, 0, 1], height: { duration: 0.25 } }}
                    className="mt-3"
                  >
                    <Row >
                      <div className="relative rounded-md border border-border bg-muted/50 p-6 w-full flex-none">
                        <Row between align>
                          <Column tightGap className="space-y-0">
                            <Text fat>
                              Dataset status
                            </Text>
                          </Column>
                          <Row align between shrink tightGap>
                            <StatusBadge status="complete" />
                          </Row>
                        </Row>

                        <motion.div className="relative">
                          <Text sm subdued className="block text-pretty">
                            {latestRunIsCurrent && view.latestRun
                              ? RUN_STATUS_META[view.latestRun.status].description
                              : view.latestRun
                                ? `A newer run is ${RUN_STATUS_META[view.latestRun.status].label.toLowerCase()}. This page stays pinned to the last complete dataset until that finishes.`
                                : RUN_STATUS_META.complete.description}
                          </Text>

                          <div className="mt-5">
                            <Row>
                              <Column className="w-1/2 pr-12">
                                <DataLine label="Contacts processed" value={(view.summary?.totalEnriched ?? 0).toLocaleString()} />
                                <DataLine label="Contacts enriched" value={(view.summary?.contactsWithProperty ?? 0).toLocaleString()} />
                                <DataLine label="Enrichment coverage" value={(propertyCoverage).toLocaleString()} />
                              </Column>
                              <Column className="w-1/2 pl-12">
                                <DataLine label="Buckets generated" value={(view.summary?.totalBuckets ?? 0).toLocaleString()} />
                                {valueSummary ? (
                                  <DataLine label="Portfolio value" value={formatDollarsShort(valueSummary.totalPortfolioValue)} />
                                ) : null}
                                <DataLine label="Data points added to your contacts" value={totalNewDataPoints.toLocaleString()} />
                              </Column>
                            </Row>
                            {view.summary?.primaryMarkets && view.summary?.primaryMarkets.length > 0 ? (
                              <div className="mt-6">
                                <PrimaryMarketsStrip markets={view.summary?.primaryMarkets ?? []} />
                              </div>
                            ) : null}
                          </div>
                        </motion.div>
                      </div>


                      {!latestRunIsCurrent && view.latestRun ? (
                        <div className="rounded-md border border-border/70 bg-white/80 px-5 py-4 shadow-xs backdrop-blur w-1/2">
                          <Row between start className="flex-wrap gap-3">
                            <Column tightGap className="space-y-0">
                              <Text fat>
                                Incoming refresh
                              </Text>
                              <Text base className="block text-foreground">
                                A newer enrichment run is currently {RUN_STATUS_META[view.latestRun.status].label.toLowerCase()}.
                              </Text>
                              {view.latestRun.bucketingStep ? (
                                <Text sm muted className="block text-pretty">
                                  {view.latestRun.bucketingStep}
                                </Text>
                              ) : null}
                            </Column>
                            <Column className="items-end gap-3">
                              <StatusBadge status={view.latestRun.status} />
                              {view.latestRun.status === "failed" ? (
                                <EnrichmentActionButton crmConnected={view.crmConnected} latestRun={view.latestRun} variant="outline" />
                              ) : null}
                            </Column>
                          </Row>
                        </div>
                      ) : null}

                      <div className="hidden">
                        {activeFeaturedContact && featuredContacts.length > 0 ? (
                          <FeaturedContactShowcase
                            contact={activeFeaturedContact}
                            bucket={activeFeaturedBucket}
                            signalCount={featuredSignalCount}
                            signals={featuredSignals}
                            index={featuredContactIndex % featuredContacts.length}
                            total={featuredContacts.length}
                            onPrevious={() => {
                              if (featuredContactIndex === 0) {
                                return;
                              }
                              setFeaturedContactIndex(featuredContactIndex - 1);
                            }}
                            onNext={() => {
                              setFeaturedContactIndex((current) =>
                                featuredContacts.length > 0 ? (current + 1) % featuredContacts.length : 0,
                              );
                            }}
                          />
                        ) : null}
                      </div>

                    </Row>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Column >

        <Column>
          <div>
            {deferredSearch && searchResults === undefined ? (
              <div className="rounded-md border border-dashed border-border/70 px-6 py-12">
                <Text sm muted>
                  Searching contacts…
                </Text>
              </div>
            ) : quickFilterExhaustedLoadedPage && canLoadMoreContacts ? (
              <div className="rounded-md border border-dashed border-border/70 px-6 py-12 bg-muted/50">
                <Column tightGap className="space-y-0">
                  <Text base fat className="block text-foreground">
                    No loaded contacts match this filter yet.
                  </Text>
                  <Text base muted className="block max-w-[52ch] text-pretty">
                    The quick filters only apply to the contacts loaded so far. Load more rows to keep scanning the rest
                    of this dataset.
                  </Text>
                </Column>
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="rounded-md border border-dashed border-border/70 px-6 py-12 bg-muted/50">
                <Column tightGap className="space-y-0">
                  <Text base fat className="block text-foreground">
                    No contacts match this view.
                  </Text>
                  <Text base muted className="block max-w-[52ch] text-pretty">
                    Try a different bucket, clear your search, or wait for the current enrichment run to finish if you
                    are expecting newer contacts to appear here.
                  </Text>
                </Column>
              </div>
            ) : (
              <ContactsTable contacts={filteredContacts} bucketById={bucketById} selectedContact={selectedContact} onSelectContact={setSelectedContact} />
            )}
          </div>
        </Column>

      </div >
    </div >
  );
}

export function Contacts() {

  const view = useQuery(api.bucketer.queries.getContactsView, {}) as ContactsViewData | undefined;

  const [showEmptyViewState, setShowEmptyViewState] = useState(true);

  useEffect(() => {
    if (view) {
      setShowEmptyViewState(!view.activeRun || !view.summary);
    }
  }, [view]);

  if (view === undefined) {
    return null;
  }

  if (showEmptyViewState) {
    return <EmptyContactsState latestRun={view.latestRun} crmConnected={view.crmConnected} />;
  }

  return <LoadedContactsView view={view} />;
}
